import { MattressProduct, ProductRecommendationResponse, MessageContent } from '../types';

/**
 * Product recommendation normalization and validation utilities.
 * These functions handle the various formats of product recommendations
 * and standardize them into a consistent format with proper validation.
 */

// Type checking utilities
const contentTypeUtils = {
  isString: (content: string | MessageContent | any): content is string => {
    return typeof content === 'string';
  },
  
  isObject: (content: any): content is Record<string, any> => {
    return typeof content === 'object' && content !== null;
  }
};

/**
 * Validates a product object to ensure it has all required fields
 * @param product The product to validate
 * @returns A validated product with default values for missing fields
 */
export const validateProduct = (product: any): MattressProduct => {
  if (!product || typeof product !== 'object') {
    throw new Error('Invalid product: must be an object');
  }
  
  if (!product.id) {
    throw new Error('Invalid product: missing id field');
  }
  
  return {
    id: product.id,
    name: product.name || 'Unnamed Product',
    description: product.description || '',
    price: typeof product.price === 'number' ? product.price : 0,
    imageUrl: product.imageUrl || '',
    productUrl: product.productUrl || '',
    features: Array.isArray(product.features) ? product.features : [],
    specifications: {
      firmness: product.specifications?.firmness || 'medium',
      type: product.specifications?.type || 'other',
      height: product.specifications?.height || 10,
      supportLevel: product.specifications?.supportLevel || 'moderate',
      coolingFeatures: !!product.specifications?.coolingFeatures,
      motionIsolation: !!product.specifications?.motionIsolation,
      edgeSupport: !!product.specifications?.edgeSupport,
      warranty: product.specifications?.warranty || ''
    },
    sleepPositions: Array.isArray(product.sleepPositions) ? product.sleepPositions : [],
    idealFor: Array.isArray(product.idealFor) ? product.idealFor : []
  };
};

/**
 * Validates an array of products and filters out invalid ones
 * @param products Array of potential product objects
 * @returns Array of validated products
 */
export const validateProducts = (products: any[]): MattressProduct[] => {
  if (!Array.isArray(products)) return [];
  
  // Filter out null/undefined and try to validate each product
  const validatedProducts: MattressProduct[] = [];
  
  products.forEach(product => {
    try {
      if (product && typeof product === 'object' && product.id) {
        validatedProducts.push(validateProduct(product));
      }
    } catch (error) {
      console.error('Error validating product:', error);
    }
  });
  
  return validatedProducts;
};

/**
 * Detects if content contains product recommendations
 * @param content Any content to check
 * @returns True if content contains product recommendations
 */
export const hasProductRecommendations = (content: any): boolean => {
  if (!contentTypeUtils.isObject(content)) return false;
  
  // Case 1: Direct product recommendations format
  if ('type' in content && 
      content.type === 'product_recommendations' && 
      'recommendations' in content) {
    return true;
  }
  
  // Case 2: Raw API response format
  if ('message_type' in content && 
      content.message_type === 'PRODUCT_RECOMMENDATIONS' && 
      'products' in content && 
      Array.isArray(content.products)) {
    return true;
  }
  
  // Case 3: If we have recommendations property with products
  if ('recommendations' in content && 
      content.recommendations && 
      'products' in content.recommendations && 
      Array.isArray(content.recommendations.products)) {
    return true;
  }
  
  return false;
};

/**
 * Attempts to parse string content as JSON for product recommendations
 * @param content String content to parse
 * @returns Parsed content or original string if parsing fails
 */
export const parseProductRecommendations = (content: string): MessageContent | string => {
  if (!content || typeof content !== 'string') return content;
  
  // Early return if content doesn't look like JSON
  const trimmedContent = content.trim();
  if (!trimmedContent.startsWith('{') || !trimmedContent.endsWith('}')) {
    return content;
  }
  
  try {
    const parsed = JSON.parse(trimmedContent);
    
    // Case 1: Direct product recommendations format
    if (parsed.type === 'product_recommendations' && parsed.recommendations) {
      return parsed;
    }
    
    // Case 2: Direct API response with message_type
    if (parsed.message_type === 'PRODUCT_RECOMMENDATIONS' && Array.isArray(parsed.products)) {
      return {
        type: 'product_recommendations',
        text: parsed.additionalMessage || 'Here are some mattress recommendations for you.',
        recommendations: parsed
      };
    }
    
    // Case 3: Wrapped product recommendations
    if (parsed.content && 
        typeof parsed.content === 'object' && 
        parsed.content.type === 'product_recommendations') {
      return parsed.content;
    }
    
    return parsed;
  } catch (error) {
    // If parsing fails, return the original content
    console.error('Error parsing product recommendations:', error);
    return content;
  }
};

/**
 * Normalizes product recommendations to ensure consistent format
 * @param content Any object that may contain product recommendations
 * @returns A standardized ProductRecommendationResponse object
 */
export const normalizeRecommendations = (content: any): ProductRecommendationResponse => {
  // Validate products to ensure fields are never null
  const validateProducts = (products: any[]): MattressProduct[] => {
    if (!Array.isArray(products)) return [];
    
    return products.map(p => ({
      id: p.id || `product-${Math.random().toString(36).substring(2, 11)}`,
      name: p.name || 'Unnamed Product',
      description: p.description || '', 
      price: p.price || 0,
      imageUrl: p.imageUrl || '',
      productUrl: p.productUrl || '',
      features: Array.isArray(p.features) ? p.features : [],
      specifications: {
        firmness: p.specifications?.firmness || 'medium',
        type: p.specifications?.type || 'other',
        height: p.specifications?.height || 10,
        supportLevel: p.specifications?.supportLevel || 'moderate',
        coolingFeatures: !!p.specifications?.coolingFeatures,
        motionIsolation: !!p.specifications?.motionIsolation,
        edgeSupport: !!p.specifications?.edgeSupport,
        warranty: p.specifications?.warranty || '10 years'
      },
      sleepPositions: Array.isArray(p.sleepPositions) ? p.sleepPositions : ['combination'],
      idealFor: Array.isArray(p.idealFor) ? p.idealFor : []
    }));
  };
  
  if (content.type === 'product_recommendations') {
    // Standard format from MessageContent type
    return {
      message_type: 'PRODUCT_RECOMMENDATIONS' as const,
      products: validateProducts(content.recommendations?.products || []),
      additionalMessage: content.text || content.recommendations?.additionalMessage || 'Here are some mattress recommendations for you.',
      storeId: content.recommendations?.storeId || ''
    };
  } else if ('message_type' in content && content.message_type === 'PRODUCT_RECOMMENDATIONS') {
    // Direct format from API
    return {
      message_type: 'PRODUCT_RECOMMENDATIONS' as const,
      products: validateProducts(content.products || []),
      additionalMessage: content.additionalMessage || 'Here are some mattress recommendations for you.',
      storeId: content.storeId || ''
    };
  }
  
  // Fallback - empty recommendation
  return { 
    message_type: 'PRODUCT_RECOMMENDATIONS' as const, 
    products: [], 
    additionalMessage: 'No recommendations available.', 
    storeId: '' 
  };
};

/**
 * Safely extracts product types from recommendations
 * @param recommendations ProductRecommendationResponse
 * @returns Array of product types without duplicates
 */
export const getProductTypes = (recommendations: ProductRecommendationResponse): string[] => {
  try {
    if (!recommendations?.products || !Array.isArray(recommendations.products)) {
      return [];
    }
    
    const types = recommendations.products
      .map(p => p.specifications?.type)
      .filter(Boolean) as string[];
      
    return [...new Set(types)].map(type => typeof type === 'string' ? type : 'other');
  } catch (error) {
    console.error('Error extracting product types:', error);
    return [];
  }
};

/**
 * Extract meaningful preferences from product recommendations
 * @param recommendations The normalized product recommendations
 * @returns Object containing extracted preferences
 */
export const extractPreferences = (recommendations: ProductRecommendationResponse): {
  productTypes: string[];
  sleepPositions: string[];
  firmness: string[];
  hasEdgeSupport: boolean;
  hasMotionIsolation: boolean;
  hasCoolingFeatures: boolean;
  hasThickerProfile: boolean;
  priceRange: { min: number; max: number } | null;
} => {
  if (!recommendations.products || recommendations.products.length === 0) {
    return {
      productTypes: [],
      sleepPositions: [],
      firmness: [],
      hasEdgeSupport: false,
      hasMotionIsolation: false,
      hasCoolingFeatures: false,
      hasThickerProfile: false,
      priceRange: null
    };
  }
  
  // Extract product types
  const productTypes = Array.from(new Set(
    recommendations.products
      .map(p => p.specifications?.type)
      .filter(Boolean)
  )).map(type => type?.replace('-', ' '));
  
  // Extract sleep positions
  const sleepPositions = Array.from(new Set(
    recommendations.products.flatMap(p => p.sleepPositions || [])
  ));
  
  // Extract firmness levels
  const firmness = Array.from(new Set(
    recommendations.products
      .map(p => p.specifications?.firmness)
      .filter(Boolean)
  ));
  
  // Check for special features
  const hasEdgeSupport = recommendations.products.some(p => p.specifications?.edgeSupport);
  const hasMotionIsolation = recommendations.products.some(p => p.specifications?.motionIsolation);
  const hasCoolingFeatures = recommendations.products.some(p => p.specifications?.coolingFeatures);
  const hasThickerProfile = recommendations.products.some(p => 
    p.specifications?.height && parseInt(p.specifications.height.toString()) >= 12
  );
  
  // Calculate price range
  const prices = recommendations.products.map(p => p.price).filter(p => p > 0);
  const priceRange = prices.length > 0 
    ? { min: Math.min(...prices), max: Math.max(...prices) }
    : null;
  
  return {
    productTypes,
    sleepPositions,
    firmness,
    hasEdgeSupport,
    hasMotionIsolation,
    hasCoolingFeatures,
    hasThickerProfile,
    priceRange
  };
};

/**
 * Creates an acknowledgment statement that reflects back the user's preferences
 * @param preferences Extracted preferences
 * @returns A string acknowledging the user's preferences
 */
export const createAcknowledgmentStatement = (preferences: ReturnType<typeof extractPreferences>): string => {
  const { sleepPositions, firmness, priceRange } = preferences;
  
  // Start with basic acknowledgment
  let acknowledgment = "Based on what you've shared";
  
  // Add sleep position if available
  if (sleepPositions.length > 0) {
    const positionText = sleepPositions.length === 1
      ? `${sleepPositions[0]} sleeping`
      : `${sleepPositions.slice(0, -1).join(', ')} and ${sleepPositions[sleepPositions.length - 1]} sleeping`;
    
    acknowledgment += ` about being a ${positionText} sleeper`;
  }
  
  // Add firmness if available
  if (firmness.length > 0) {
    const firmnessText = firmness.length === 1
      ? `${firmness[0]} firmness`
      : `${firmness.join(' to ')} firmness range`;
    
    acknowledgment += sleepPositions.length > 0
      ? ` who prefers ${firmnessText}`
      : ` about your preference for ${firmnessText}`;
  }
  
  // Add price range if available
  if (priceRange) {
    const formattedMin = priceRange.min.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    const formattedMax = priceRange.max.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    
    if (priceRange.max - priceRange.min > 300) {
      acknowledgment += ` within a budget of ${formattedMin} to ${formattedMax}`;
    } else {
      acknowledgment += ` with a budget around ${formattedMax}`;
    }
  }
  
  return acknowledgment;
};

/**
 * Create educational content about mattress types based on user preferences
 * @param preferences Extracted preferences
 * @returns A string explaining mattress types and features relevant to user
 */
export const createEducationalContent = (preferences: ReturnType<typeof extractPreferences>): string => {
  const { productTypes, sleepPositions, firmness } = preferences;
  
  let education = "";
  
  // Only proceed if we have actual product types to recommend
  if (productTypes.length === 0) {
    return education;
  }
  
  // Create precise mattress type recommendations that include firmness + type
  const mattressTypeRecommendations: string[] = [];
  
  // Combine firmness and type if available
  if (firmness.length > 0) {
    firmness.forEach(firm => {
      productTypes.forEach(type => {
        // Format the type and capitalize first letter
        const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
        const formattedFirm = firm.charAt(0).toUpperCase() + firm.slice(1);
        mattressTypeRecommendations.push(`${formattedFirm} ${formattedType}`);
      });
    });
  } else {
    // If no firmness specified, just use the product types
    productTypes.forEach(type => {
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
      mattressTypeRecommendations.push(formattedType);
    });
  }
  
  // Make sure we have recommendations
  if (mattressTypeRecommendations.length === 0) {
    return education;
  }
  
  // Add the recommendation phrase
  education += `, I'd recommend `;
  
  if (mattressTypeRecommendations.length === 1) {
    education += `a ${mattressTypeRecommendations[0]} mattress`;
  } else if (mattressTypeRecommendations.length === 2) {
    education += `either a ${mattressTypeRecommendations[0]} or ${mattressTypeRecommendations[1]} mattress`;
  } else {
    const lastType = mattressTypeRecommendations.pop();
    education += `one of these mattress types: ${mattressTypeRecommendations.join(', ')}, or ${lastType}`;
  }
  
  // Explain why certain mattress types are good for their sleep position
  if (sleepPositions.length > 0) {
    if (sleepPositions.some(pos => pos.toLowerCase().includes('side'))) {
      education += `. This type of mattress will provide the right balance of contouring and support for side sleeping, helping relieve pressure points at your shoulders and hips`;
    } else if (sleepPositions.some(pos => pos.toLowerCase().includes('back'))) {
      education += `. This type of mattress offers the support needed for back sleeping, maintaining proper spinal alignment while providing comfort`;
    } else if (sleepPositions.some(pos => pos.toLowerCase().includes('stomach'))) {
      education += `. This firmer support is ideal for stomach sleeping to prevent your midsection from sinking too deeply and straining your lower back`;
    } else if (sleepPositions.some(pos => pos.toLowerCase().includes('combination'))) {
      education += `. This versatile support works well for combination sleeping, allowing easier movement throughout the night as you change positions`;
    }
  }
  
  return education;
};

/**
 * Create a concluding statement that introduces the product recommendations
 * @returns A string that leads into the product recommendations
 */
export const createConcludingStatement = (): string => {
  // Use a single, clear concluding statement
  return "Here are the mattresses that best match your needs:";
};

/**
 * Generates a personalized introduction for product recommendations
 * @param recommendations Product recommendation data
 * @returns A personalized introduction string
 */
export const getPersonalizedIntro = (recommendations: ProductRecommendationResponse): string => {
  try {
    // Check if we have valid products to analyze
    if (!recommendations.products || recommendations.products.length === 0) {
      return "Here are some mattress options that might work for you:";
    }
    
    // Extract preferences from recommendations
    const preferences = extractPreferences(recommendations);
    
    // Build the personalized message in sections
    const acknowledgment = createAcknowledgmentStatement(preferences);
    const education = createEducationalContent(preferences);
    
    // Combine sections into a complete message (don't add the period if education is empty)
    return education ? `${acknowledgment}${education}.` : `${acknowledgment}.`;
  } catch (error) {
    console.error('Error generating personalized intro:', error);
    return "Here are some mattress recommendations that might work for you:";
  }
}; 